import React, { useCallback } from 'react';
import LabelAndHelper from '../../../../common/forms/LabelAndHelper';
import { Box, makeStyles, Input, Switch } from '@material-ui/core';
import TimeBox from '../../../pieces/TimeBox';
import PropTypes from 'prop-types';
import Subtitle2 from '../../../../common/typographys/Subtitle2';
import InputField from '../../../../common/forms/InputField';

const useStyles = makeStyles(theme => ({
  periodSection: {
    marginBottom: '16px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  periodHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // marginBottom: '12px',
  },
  periodTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  periodContent: {
    paddingLeft: '8px',
  },
  periodContentDisabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
  },
  fieldLabel: {
    minWidth: '80px',
    marginRight: '12px',
  },
  titleInput: {
    flex: 1,
  },
  timeInputBox: {
    padding: '8px',
    width: '114px',
    backgroundColor: theme.palette.primary[200],
    height: '40px',
  },
  inputRoot: {
    ...theme.typography.body1,
    height: theme.typography.body1.lineHeight,
    minHeight: '0px',
  },
  input: {
    padding: 0,
    ...theme.typography.body1,
    height: theme.typography.body1.lineHeight,
    color: theme.palette.neutrals[7],
  },
  divider: {
    borderBottom: `1px solid ${theme.palette.neutrals[3]}`,
    marginBottom: '16px',
  },
}));

/**
 * Converte segundos para string no formato "HH:MM:SS"
 */
const secondsToTimeString = (seconds) => {
  if (!seconds || seconds < 0) return '00:00:00';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return [hours, minutes, secs]
    .map(v => String(v).padStart(2, '0'))
    .join(':');
};

/**
 * Converte string no formato "HH:MM:SS" para segundos
 */
const timeStringToSeconds = (timeString) => {
  if (!timeString) return 0;

  const parts = timeString.split(':').map(Number);

  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }

  return 0;
};

const PeriodSection = ({
  label,
  period,
  name,
  onChange,
  showDivider,
  hideTimer
}) => {
  const classes = useStyles();

  const handleEnabledChange = (e) => {
    onChange(name, {
      ...period,
      enabled: e.target.checked,
    });
  };

  const handleTitleChange = (e) => {
    onChange(name, {
      ...period,
      title: e.target.value,
    });
  };

  const handleTimeEnabledChange = (e) => {
    onChange(name, {
      ...period,
      time_enabled: e.target.checked,
    });
  };

  const handleTimeChange = (e) => {
    const seconds = timeStringToSeconds(e.target.value);
    onChange(name, {
      ...period,
      time: seconds,
    });
  };

  // const isEnabled = period?.enabled ?? false;
  const isEnabled = true;

  const isTimeEnabled = period?.time_enabled ?? false;
  const timeValue = secondsToTimeString(period?.time);

  return (
    <>
      <Box className={classes.periodSection}>
        <Box className={classes.periodHeader}>
          <Box className={classes.periodTitle}>
            <Subtitle2>{label}</Subtitle2>
          </Box>
          {/* <Switch
            checked={isEnabled}
            onChange={handleEnabledChange}
            color="primary"
          /> */}
        </Box>
        <Box
          className={`${classes.periodContent} ${!isEnabled ? classes.periodContentDisabled : ''}`}
        >
          <Box className={classes.fieldRow}>
            <Subtitle2 className={classes.fieldLabel}>Título</Subtitle2>
            <Box className={classes.titleInput}>
              <InputField
                name={`${name}-title`}
                value={period?.title || ''}
                placeholder={label}
                onChange={handleTitleChange}
                disabled={!isEnabled}
              />
            </Box>
          </Box>

          <Box height="10px" />

          {!hideTimer && (
            <Box className={classes.fieldRow}>
              <Subtitle2 className={classes.fieldLabel}>Tempo</Subtitle2>
              <Switch
                checked={isTimeEnabled}
                onChange={handleTimeEnabledChange}
                color="primary"
              />
              <Box className={classes.timeInputBox}>
                <Input
                  classes={{ input: classes.input, root: classes.inputRoot }}
                  value={timeValue}
                  type="time"
                  inputProps={{
                    step: 5,
                  }}
                  disableUnderline
                  onChange={handleTimeChange}
                  disabled={!isTimeEnabled}
                />
              </Box>
            </Box>
          )}
          {hideTimer && (
            <div>Tempo crescente</div>
          )}
        </Box>
      </Box>
      {showDivider && <Box className={classes.divider} />}
    </>
  );
};

PeriodSection.propTypes = {
  label: PropTypes.string.isRequired,
  period: PropTypes.shape({
    enabled: PropTypes.bool,
    title: PropTypes.string,
    time: PropTypes.number,
  }),
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  showDivider: PropTypes.bool,
  hideTimer: PropTypes.bool,
};

PeriodSection.defaultProps = {
  period: {
    title: '',
    enabled: true,
    time_enabled: true,
    time: 0,
  },
  showDivider: true,
  hideTimer: false,
};

const TypologyPeriods = ({ periods, updateField }) => {
  const handlePeriodChange = useCallback(
    (name, value) => {
      updateField('periods', {
        ...periods,
        [name]: value,
      });
    },
    [periods, updateField]
  );

  return (
    <>
      <LabelAndHelper
        label="Configuração de períodos"
        helper="Defina os timers dos períodos para esta tipologia de reunião."
      />
      <Box height="12px" />
      <TimeBox medium>
        <PeriodSection
          label="Intervenção do público"
          period={periods?.publicDiscussion}
          name="publicDiscussion"
          onChange={handlePeriodChange}
        />
        <PeriodSection
          label="Antes da ordem do dia"
          period={periods?.beforeDayOrder}
          name="beforeDayOrder"
          onChange={handlePeriodChange}
          hideTimer
        />
        <PeriodSection
          label="Apresentação da proposta"
          period={periods?.presentation}
          name="presentation"
          onChange={handlePeriodChange}
        />
        <PeriodSection
          label="Protestos / defesa de honra / reações"
          period={periods?.protests}
          name="protests"
          onChange={handlePeriodChange}
          showDivider={false}
        />
      </TimeBox>
    </>
  );
};

TypologyPeriods.propTypes = {
  periods: PropTypes.shape({
    publicDiscussion: PropTypes.shape({
      enabled: PropTypes.bool,
      title: PropTypes.string,
      time: PropTypes.number,
    }),
    beforeDayOrder: PropTypes.shape({
      enabled: PropTypes.bool,
      title: PropTypes.string,
      time: PropTypes.number,
    }),
    presentation: PropTypes.shape({
      enabled: PropTypes.bool,
      title: PropTypes.string,
      time: PropTypes.number,
    }),
    protests: PropTypes.shape({
      enabled: PropTypes.bool,
      title: PropTypes.string,
      time: PropTypes.number,
    }),
  }),
  updateField: PropTypes.func.isRequired,
};

TypologyPeriods.defaultProps = {
  periods: {
    publicDiscussion: {
      title: '',
      enabled: true,
      time_enabled: true,
      time: 5 * 60,
    },
    beforeDayOrder: {
      title: '',
      enabled: true,
      time_enabled: true,
      time: 0,
    },
    presentation: {
      title: '',
      enabled: true,
      time_enabled: true,
      time: 3 * 60,
    },
    protests: {
      title: '',
      enabled: true,
      time_enabled: true,
      time: 3 * 60,
    },
  },
};

export default React.memo(TypologyPeriods);
